import { FC, ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TokenIcon } from "@/components/TokenIcon";

const TIGHT_INSET_SURFACE_SHADOW =
  "inset 0 0 0 0.5px rgba(255, 255, 255, 0.06), inset 0 0.5px 0.5px rgba(255, 255, 255, 0.02)";

export interface AssetAmountFieldToken {
  assetId: string;
  name: string;
}

interface AssetAmountFieldProps {
  amount: string;
  label: string;
  placeholder?: string;
  onAmountChange?: (value: string) => void;
  token: AssetAmountFieldToken;
  tokens?: AssetAmountFieldToken[];
  usedAssetIds?: Set<string>;
  onSelectToken?: (assetId: string) => void;
  helperText?: ReactNode;
  footerText?: ReactNode;
  errorText?: string;
  isError?: boolean;
  isShaking?: boolean;
  autoFocus?: boolean;
  actionSlot?: ReactNode;
  rightTopContent?: ReactNode;
  amountClassName?: string;
  inputMode?: "decimal" | "numeric" | "text";
  readOnly?: boolean;
  tokenSurfaceColor?: string;
  surfaceColor?: string;
  className?: string;
  bottomContent?: ReactNode;
}


function TokenSelector({
  selected,
  tokens,
  usedAssetIds,
  onSelect,
  surfaceColor,
}: {
  selected: AssetAmountFieldToken;
  tokens: AssetAmountFieldToken[];
  usedAssetIds: Set<string>;
  onSelect: (assetId: string) => void;
  surfaceColor: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  // Nothing to select (e.g. no depositable assets): render nothing rather than
  // dereferencing an undefined token.
  if (!selected) return null;

  const available = tokens.filter(
    (t) => t.assetId === selected.assetId || !usedAssetIds.has(t.assetId),
  );

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="flex items-center gap-2 rounded-full px-3 py-2 transition-colors hover:brightness-110"
        style={{
          backgroundColor: surfaceColor,
          boxShadow: TIGHT_INSET_SURFACE_SHADOW,
        }}
      >
        <TokenIcon name={selected.name} className="w-6 h-6" />
        <span className="text-base font-medium text-white">{selected.name}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-sr-neutral-500">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[61] min-w-[160px] max-h-[240px] overflow-y-auto rounded-xl shadow-xl"
            style={{
              top: pos.top,
              right: pos.right,
              backgroundColor: surfaceColor,
              boxShadow: TIGHT_INSET_SURFACE_SHADOW,
            }}
          >
            {available.length === 0 ? (
              <div className="px-3 py-3 text-sm text-sr-neutral-500">
                No options
              </div>
            ) : available.map((item) => (
              <button
                key={item.assetId}
                onClick={() => { onSelect(item.assetId); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-sr-surface-raised transition-colors ${
                  item.assetId === selected.assetId ? "bg-sr-surface-raised" : ""
                }`}
              >
                <TokenIcon name={item.name} className="w-5 h-5" />
                <span className="text-sm text-white">{item.name}</span>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export const AssetAmountField: FC<AssetAmountFieldProps> = ({
  amount,
  label,
  placeholder = "0.00",
  onAmountChange,
  token,
  tokens,
  usedAssetIds,
  onSelectToken,
  helperText,
  footerText,
  errorText,
  isError = false,
  isShaking = false,
  autoFocus = false,
  actionSlot,
  rightTopContent,
  amountClassName = "text-[28px]",
  inputMode = "decimal",
  readOnly = false,
  tokenSurfaceColor = "#2F1C25",
  surfaceColor,
  className = "",
  bottomContent,
}) => {
  const showSelector = tokens && onSelectToken;

  return (
    <div
      className={`sr-card min-w-0 p-4 transition-shadow focus-within:shadow-[inset_0_0_0_0.5px_rgba(255,193,244,0.08),inset_0_4px_10px_rgba(255,193,244,0.025)] ${className}`}
      style={surfaceColor ? { backgroundColor: surfaceColor } : undefined}
    >
      <div className="flex min-w-0 items-start justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs text-sr-bubblegum tracking-tight">
            {label}
          </span>
          <input
            type="text"
            inputMode={inputMode}
            value={amount}
            onChange={(e) => onAmountChange?.(e.target.value)}
            placeholder={placeholder}
            readOnly={readOnly}
            className={`w-full min-w-0 bg-transparent font-medium outline-none placeholder:text-sr-neutral-700 tabular-nums leading-tight ${amountClassName} ${isError ? "text-sr-salmon" : "text-white"} ${isShaking ? "sr-input-shake" : ""} ${readOnly ? "cursor-default" : ""}`}
            style={{ fontFamily: "var(--font-display)" }}
            autoFocus={autoFocus}
          />
          {errorText ? (
            <p className="text-xs text-sr-salmon">{errorText}</p>
          ) : helperText ? (
            <div className="text-[11px] text-sr-neutral-500">{helperText}</div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {rightTopContent}
          <div className="relative">
            {showSelector ? (
              <TokenSelector
                selected={token}
                tokens={tokens}
                usedAssetIds={usedAssetIds ?? new Set()}
                onSelect={onSelectToken}
                surfaceColor={tokenSurfaceColor}
              />
            ) : (
              <div
                className="flex items-center gap-2 rounded-full px-3 py-2"
                style={{
                  backgroundColor: tokenSurfaceColor,
                  boxShadow: TIGHT_INSET_SURFACE_SHADOW,
                }}
              >
                <TokenIcon name={token.name} className="w-6 h-6" />
                <span className="text-base font-medium text-white">{token.name}</span>
              </div>
            )}
            {actionSlot}
          </div>
          {footerText ? (
            <div className="text-right text-xs leading-normal text-sr-pink-muted">
              {footerText}
            </div>
          ) : null}
        </div>
      </div>
      {bottomContent ? (
        <div className="mt-3 flex flex-col gap-2">
          {bottomContent}
        </div>
      ) : null}
    </div>
  );
};
